import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CHAIN_CATEGORY_DOT_COLORS } from '@/types'
import type { ChainDefinitionCategory, ChainDefinitionSummary } from '@/types'
import { ChainCard } from './ChainCard'

type SectionCategory = ChainDefinitionCategory | 'Uncategorized'

interface ChainCategorySectionProps {
  category: SectionCategory
  chains: ChainDefinitionSummary[]
  isExpanded: boolean
  onToggle: () => void
  canStart: boolean
  canDuplicate: boolean
  canToggle: boolean
  canDelete: boolean
  onNavigate: (chain: ChainDefinitionSummary) => void
  onStart: (chain: ChainDefinitionSummary) => void
  onDuplicate: (chain: ChainDefinitionSummary) => void
  onToggleActive: (chain: ChainDefinitionSummary) => void
  onDelete: (chain: ChainDefinitionSummary) => void
}

export function ChainCategorySection({
  category,
  chains,
  isExpanded,
  onToggle,
  ...cardProps
}: ChainCategorySectionProps) {
  return (
    <div data-testid={`chain-category-section-${category}`}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-muted transition-colors text-left"
        aria-expanded={isExpanded}
        data-testid={`chain-category-section-header-${category}`}
      >
        <ChevronDown
          className={cn(
            'h-4 w-4 text-foreground transition-transform duration-200 shrink-0',
            isExpanded ? 'rotate-0' : '-rotate-90',
          )}
        />
        <span className={cn('h-2.5 w-2.5 rounded-full shrink-0', CHAIN_CATEGORY_DOT_COLORS[category])} />
        <span className="text-sm font-bold leading-[1.6] tracking-[0.015em] text-foreground">
          {category}
        </span>
        <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-button text-button-foreground text-[10px] font-semibold shrink-0">
          {chains.length}
        </span>
      </button>

      {isExpanded && (
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {chains.map((c) => (
            <ChainCard key={c.id} chain={c} {...cardProps} />
          ))}
        </div>
      )}
    </div>
  )
}
